import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateClassTypeDTO,
  CreateInstructorDTO,
  CreateLevelDTO,
  CreateRoomDTO,
  CreateStudentGroupDTO,
  ReorderLevelsDTO,
  ReorderStudentGroupsDTO,
  UpdateClassTypeDTO,
  UpdateInstructorDTO,
  UpdateLevelDTO,
  UpdateRoomDTO,
  UpdateStudentGroupDTO,
} from "@/modules/master/domain/contracts";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { masterApi } from "./client";

// ===== Class Types =====
export const classTypesKeys = {
  all: ["master", "class-types"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) =>
    ["master", "class-types", "list", q] as const,
  detail: (id: string) => ["master", "class-types", "detail", id] as const,
};

export function useClassTypesQuery(query: Partial<OffsetPageQueryDTO> = {}) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: classTypesKeys.list(q),
    queryFn: () => masterApi.classTypes.list(q),
  });
}

export function useClassTypeQuery(id: string) {
  return useQuery({
    queryKey: classTypesKeys.detail(id),
    queryFn: () => masterApi.classTypes.get(id),
    enabled: !!id,
  });
}

export function useCreateClassType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateClassTypeDTO) =>
      masterApi.classTypes.create(input),
    onSuccess: () => {
      toast.success("ສ້າງປະເພດຄລາດສ໌ສຳເລັດ");
      qc.invalidateQueries({ queryKey: classTypesKeys.all });
    },
    onError: () => toast.error("ສ້າງປະເພດຄລາດສ໌ບໍ່ສຳເລັດ"),
  });
}

export function useUpdateClassType(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateClassTypeDTO) =>
      masterApi.classTypes.update(id, input),
    onSuccess: () => {
      toast.success("ອັບເດດປະເພດຄລາດສ໌ສຳເລັດ");
      qc.invalidateQueries({ queryKey: classTypesKeys.detail(id) });
      qc.invalidateQueries({ queryKey: classTypesKeys.all });
    },
    onError: () => toast.error("ອັບເດດປະເພດຄລາດສ໌ບໍ່ສຳເລັດ"),
  });
}

export function useDeleteClassType() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) => masterApi.classTypes.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: classTypesKeys.all });
    },
    onError: () => toast.error("ລຶບປະເພດຄລາດສ໌ບໍ່ສຳເລັດ"),
  });

  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });

  return { ...base, run };
}

// ===== Levels =====
export const levelsKeys = {
  all: ["master", "levels"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) =>
    ["master", "levels", "list", q] as const,
  detail: (id: string) => ["master", "levels", "detail", id] as const,
};

export function useLevelsQuery(query: Partial<OffsetPageQueryDTO> = {}) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: levelsKeys.list(q),
    queryFn: () => masterApi.levels.list(q),
  });
}

export function useLevelQuery(id: string) {
  return useQuery({
    queryKey: levelsKeys.detail(id),
    queryFn: () => masterApi.levels.get(id),
    enabled: !!id,
  });
}

export function useCreateLevel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateLevelDTO) => masterApi.levels.create(input),
    onSuccess: () => {
      toast.success("ສ້າງລະດັບສຳເລັດ");
      qc.invalidateQueries({ queryKey: levelsKeys.all });
    },
    onError: () => toast.error("ສ້າງລະດັບບໍ່ສຳເລັດ"),
  });
}

export function useUpdateLevel(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateLevelDTO) => masterApi.levels.update(id, input),
    onSuccess: () => {
      toast.success("ອັບເດດລະດັບສຳເລັດ");
      qc.invalidateQueries({ queryKey: levelsKeys.detail(id) });
      qc.invalidateQueries({ queryKey: levelsKeys.all });
    },
    onError: () => toast.error("ອັບເດດລະດັບບໍ່ສຳເລັດ"),
  });
}

export function useReorderLevels() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ReorderLevelsDTO) => masterApi.levels.reorder(input),
    onSuccess: () => {
      toast.success("ຈັດລຳດັບລະດັບສຳເລັດ");
      qc.invalidateQueries({ queryKey: levelsKeys.all });
    },
    onError: () => toast.error("ຈັດລຳດັບລະດັບບໍ່ສຳເລັດ"),
  });
}

export function useDeleteLevel() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) => masterApi.levels.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: levelsKeys.all });
    },
    onError: () => toast.error("ລຶບລະດັບບໍ່ສຳເລັດ"),
  });

  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });

  return { ...base, run };
}

// ===== Rooms =====
export const roomsKeys = {
  all: ["master", "rooms"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) =>
    ["master", "rooms", "list", q] as const,
  detail: (id: string) => ["master", "rooms", "detail", id] as const,
};

export function useRoomsQuery(query: Partial<OffsetPageQueryDTO> = {}) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: roomsKeys.list(q),
    queryFn: () => masterApi.rooms.list(q),
  });
}

export function useRoomQuery(id: string) {
  return useQuery({
    queryKey: roomsKeys.detail(id),
    queryFn: () => masterApi.rooms.get(id),
    enabled: !!id,
  });
}

export function useCreateRoom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRoomDTO) => masterApi.rooms.create(input),
    onSuccess: () => {
      toast.success("ສ້າງຫ້ອງຮຽນສຳເລັດ");
      qc.invalidateQueries({ queryKey: roomsKeys.all });
    },
    onError: () => toast.error("ສ້າງຫ້ອງຮຽນບໍ່ສຳເລັດ"),
  });
}

export function useUpdateRoom(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateRoomDTO) => masterApi.rooms.update(id, input),
    onSuccess: () => {
      toast.success("ອັບເດດຫ້ອງຮຽນສຳເລັດ");
      qc.invalidateQueries({ queryKey: roomsKeys.detail(id) });
      qc.invalidateQueries({ queryKey: roomsKeys.all });
    },
    onError: () => toast.error("ອັບເດດຫ້ອງຮຽນບໍ່ສຳເລັດ"),
  });
}

export function useDeleteRoom() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) => masterApi.rooms.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: roomsKeys.all });
    },
    onError: () => toast.error("ລຶບຫ້ອງຮຽນບໍ່ສຳເລັດ"),
  });

  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });

  return { ...base, run };
}

// ===== Instructors =====
export const instructorsKeys = {
  all: ["master", "instructors"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) =>
    ["master", "instructors", "list", q] as const,
  detail: (id: string) => ["master", "instructors", "detail", id] as const,
  summary: (id: string) =>
    ["master", "instructors", "summary", id] as const,
  classSessions: (id: string, q: Partial<OffsetPageQueryDTO>) =>
    ["master", "instructors", "class-sessions", id, q] as const,
  privateLessons: (id: string, q: Partial<OffsetPageQueryDTO>) =>
    ["master", "instructors", "private-lessons", id, q] as const,
  checkIns: (id: string, q: Partial<OffsetPageQueryDTO>) =>
    ["master", "instructors", "check-ins", id, q] as const,
};

export function useInstructorsQuery(query: Partial<OffsetPageQueryDTO> = {}) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: instructorsKeys.list(q),
    queryFn: () => masterApi.instructors.list(q),
  });
}

export function useInstructorQuery(id: string) {
  return useQuery({
    queryKey: instructorsKeys.detail(id),
    queryFn: () => masterApi.instructors.get(id),
    enabled: !!id,
  });
}

export function useInstructorSummaryQuery(id: string) {
  return useQuery({
    queryKey: instructorsKeys.summary(id),
    queryFn: () => masterApi.instructors.getSummary(id),
    enabled: !!id,
  });
}

export function useInstructorClassSessionsQuery(
  id: string,
  query: Partial<OffsetPageQueryDTO> = {},
) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: instructorsKeys.classSessions(id, q),
    queryFn: () => masterApi.instructors.listClassSessions(id, q),
    enabled: !!id,
  });
}

export function useInstructorPrivateLessonsQuery(
  id: string,
  query: Partial<OffsetPageQueryDTO> = {},
) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: instructorsKeys.privateLessons(id, q),
    queryFn: () => masterApi.instructors.listPrivateLessons(id, q),
    enabled: !!id,
  });
}

export function useInstructorCheckInsQuery(
  id: string,
  query: Partial<OffsetPageQueryDTO> = {},
) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: instructorsKeys.checkIns(id, q),
    queryFn: () => masterApi.instructors.listCheckIns(id, q),
    enabled: !!id,
  });
}

export function useCreateInstructor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateInstructorDTO) =>
      masterApi.instructors.create(input),
    onSuccess: () => {
      toast.success("ສ້າງຄູສອນສຳເລັດ");
      qc.invalidateQueries({ queryKey: instructorsKeys.all });
    },
    onError: () => toast.error("ສ້າງຄູສອນບໍ່ສຳເລັດ"),
  });
}

export function useUpdateInstructor(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateInstructorDTO) =>
      masterApi.instructors.update(id, input),
    onSuccess: () => {
      toast.success("ອັບເດດຄູສອນສຳເລັດ");
      qc.invalidateQueries({ queryKey: instructorsKeys.detail(id) });
      qc.invalidateQueries({ queryKey: instructorsKeys.all });
    },
    onError: () => toast.error("ອັບເດດຄູສອນບໍ່ສຳເລັດ"),
  });
}

export function useDeleteInstructor() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) => masterApi.instructors.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: instructorsKeys.all });
    },
    onError: () => toast.error("ລຶບຄູສອນບໍ່ສຳເລັດ"),
  });

  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });

  return { ...base, run };
}

// ===== Student Groups =====
export const studentGroupsKeys = {
  all: ["master", "student-groups"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) =>
    ["master", "student-groups", "list", q] as const,
  detail: (id: string) => ["master", "student-groups", "detail", id] as const,
};

export function useStudentGroupsQuery(query: Partial<OffsetPageQueryDTO> = {}) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: studentGroupsKeys.list(q),
    queryFn: () => masterApi.studentGroups.list(q),
  });
}

export function useStudentGroupQuery(id: string) {
  return useQuery({
    queryKey: studentGroupsKeys.detail(id),
    queryFn: () => masterApi.studentGroups.get(id),
    enabled: !!id,
  });
}

export function useCreateStudentGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateStudentGroupDTO) =>
      masterApi.studentGroups.create(input),
    onSuccess: () => {
      toast.success("ສ້າງກຸ່ມນັກຮຽນສຳເລັດ");
      qc.invalidateQueries({ queryKey: studentGroupsKeys.all });
    },
    onError: () => toast.error("ສ້າງກຸ່ມນັກຮຽນບໍ່ສຳເລັດ"),
  });
}

export function useUpdateStudentGroup(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateStudentGroupDTO) =>
      masterApi.studentGroups.update(id, input),
    onSuccess: () => {
      toast.success("ອັບເດດກຸ່ມນັກຮຽນສຳເລັດ");
      qc.invalidateQueries({ queryKey: studentGroupsKeys.detail(id) });
      qc.invalidateQueries({ queryKey: studentGroupsKeys.all });
    },
    onError: () => toast.error("ອັບເດດກຸ່ມນັກຮຽນບໍ່ສຳເລັດ"),
  });
}

export function useReorderStudentGroups() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ReorderStudentGroupsDTO) =>
      masterApi.studentGroups.reorder(input),
    onSuccess: () => {
      toast.success("ຈັດລຳດັບກຸ່ມນັກຮຽນສຳເລັດ");
      qc.invalidateQueries({ queryKey: studentGroupsKeys.all });
    },
    onError: () => toast.error("ຈັດລຳດັບກຸ່ມນັກຮຽນບໍ່ສຳເລັດ"),
  });
}

export function useDeleteStudentGroup() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) => masterApi.studentGroups.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: studentGroupsKeys.all });
    },
    onError: () => toast.error("ລຶບກຸ່ມນັກຮຽນບໍ່ສຳເລັດ"),
  });

  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });

  return { ...base, run };
}
